(function () {
  const DESKTOP_MQ = window.matchMedia('(min-width: 769px)');

  function getHeaderOffset() {
    const stickyHeader = document.querySelector('sticky-header');
    if (!stickyHeader) return 0;
    return Math.round(stickyHeader.getBoundingClientRect().height);
  }

  function applyHeaderOffset(section) {
    section.style.setProperty('--spc-header-offset', `${getHeaderOffset()}px`);
  }

  function getStickyHeaderGap(section) {
    return Number.parseFloat(section?.dataset?.stickyHeaderGap) || 0;
  }

  function isMobileScrollEnabled(section) {
    return section?.dataset?.mobileScrollEnabled === 'true';
  }

  function getScrollStep(section) {
    return Number.parseFloat(section?.dataset?.scrollStep) || 60;
  }

  function getScrollDistance(section, totalSlides) {
    if (totalSlides <= 1) return 0;

    const steps = totalSlides - 1;
    const stepSize = DESKTOP_MQ.matches ? getScrollStep(section) : getMobileScrollStep(section);
    return Math.round((window.innerHeight * stepSize * steps) / 100);
  }

  function getLastSlideHold(section, totalSlides) {
    if (totalSlides <= 1 || !DESKTOP_MQ.matches) return 0;
    // Keep the last product fully in view briefly before the next section enters
    return Math.round(window.innerHeight * 0.3);
  }

  function getSlideProgress(progress, section, totalSlides) {
    const distance = getScrollDistance(section, totalSlides);
    const hold = getLastSlideHold(section, totalSlides);
    const total = distance + hold;
    if (distance <= 0 || total <= 0) return progress;
    return Math.min(1, (progress * total) / distance);
  }

  function getStepSnapPoint(progress, currentIndex, totalSlides, section) {
    if (totalSlides <= 1) return 0;

    const maxIndex = totalSlides - 1;
    const ratio = distanceRatio(section, totalSlides);
    const slideProgress = getSlideProgress(progress, section, totalSlides);
    const targetIndex = Math.round(slideProgress * maxIndex);

    // Let the last-slide hold zone scroll freely until the next section enters
    if (currentIndex === maxIndex && progress >= ratio) {
      return progress;
    }

    if (targetIndex === currentIndex) {
      return (currentIndex / maxIndex) * ratio;
    }

    const direction = targetIndex > currentIndex ? 1 : -1;
    const nextIndex = Math.max(0, Math.min(maxIndex, currentIndex + direction));
    return (nextIndex / maxIndex) * ratio;
  }

  function distanceRatio(section, totalSlides) {
    const distance = getScrollDistance(section, totalSlides);
    const hold = getLastSlideHold(section, totalSlides);
    const total = distance + hold;
    return total > 0 ? distance / total : 1;
  }

  function getStepIndex(progress, totalSlides, section) {
    if (totalSlides <= 1) return 0;
    const slideProgress = section
      ? getSlideProgress(progress, section, totalSlides)
      : progress;
    return Math.round(slideProgress * (totalSlides - 1));
  }

  function getNextStepIndex(progress, currentIndex, totalSlides, section) {
    const targetIndex = getStepIndex(progress, totalSlides, section);
    if (targetIndex === currentIndex) return currentIndex;

    const direction = targetIndex > currentIndex ? 1 : -1;
    return Math.max(0, Math.min(totalSlides - 1, currentIndex + direction));
  }

  function getMobileScrollStep(section) {
    return Number.parseFloat(section?.dataset?.mobileScrollStep) || 120;
  }

  function getMobileSwitchDelay(section) {
    const mobileScrollStep = getMobileScrollStep(section);
    return Math.max(160, Math.min(420, mobileScrollStep * 2));
  }

  function getPinOffset(section) {
    const desktopGap = DESKTOP_MQ.matches ? getStickyHeaderGap(section) : 0;
    return getHeaderOffset() + desktopGap;
  }

  function getPinScrollEnd(section, totalSlides) {
    const distance = getScrollDistance(section, totalSlides);
    if (distance <= 0) return 'bottom bottom';
    return `+=${distance + getLastSlideHold(section, totalSlides)}`;
  }

  function destroyScrollProductCarousel(section) {
    if (!section) return;

    destroyScrollProductCarouselHotspots(section);

    section._spcTriggers?.forEach((trigger) => trigger.kill());
    section._spcTriggers = [];

    section._spcNavHandlers?.forEach(({ button, handler }) => {
      button.removeEventListener('click', handler);
    });
    section._spcNavHandlers = [];

    if (section._spcNavReleaseTimer) {
      window.clearTimeout(section._spcNavReleaseTimer);
      section._spcNavReleaseTimer = null;
    }

    section._spcResizeObserver?.disconnect();
    section._spcResizeObserver = null;

    section._spcHeaderObserver?.disconnect();
    section._spcHeaderObserver = null;

    section._spcMediaHandler?.();
    section._spcMediaHandler = null;

    section.style.removeProperty('--spc-header-offset');
    section.querySelector('.spc-pin-panel')?.style.removeProperty('top');
  }

  function scrollToSlide(scrollTrigger, slideIndex, totalSlides, onComplete, section) {
    if (!scrollTrigger || totalSlides <= 1) {
      onComplete?.();
      return;
    }

    const ratio = section ? distanceRatio(section, totalSlides) : 1;
    const progress = (slideIndex / (totalSlides - 1)) * ratio;
    const targetScroll = scrollTrigger.start + progress * (scrollTrigger.end - scrollTrigger.start);
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const finish = () => onComplete?.();

    if (reducedMotion) {
      window.scrollTo(0, targetScroll);
      finish();
      return;
    }

    window.scrollTo({
      top: targetScroll,
      behavior: 'smooth',
    });

    const startedAt = performance.now();
    const maxWait = 2000;
    let stableFrames = 0;
    let lastScrollY = window.scrollY;

    const poll = () => {
      const currentScrollY = window.scrollY;
      const reachedTarget = Math.abs(currentScrollY - targetScroll) < 2;
      const scrollSettled = Math.abs(currentScrollY - lastScrollY) < 0.5;
      const timedOut = performance.now() - startedAt > maxWait;

      stableFrames = reachedTarget && scrollSettled ? stableFrames + 1 : 0;
      lastScrollY = currentScrollY;

      if (stableFrames >= 3 || timedOut) {
        finish();
        return;
      }

      requestAnimationFrame(poll);
    };

    requestAnimationFrame(poll);
  }

  function destroyScrollProductCarouselHotspots(section) {
    if (!section) return;

    section._spcHotspotHandlers?.forEach(({ element, type, handler }) => {
      element.removeEventListener(type, handler);
    });
    section._spcHotspotHandlers = [];

    if (section._spcHotspotKeyHandler) {
      document.removeEventListener('keydown', section._spcHotspotKeyHandler);
      section._spcHotspotKeyHandler = null;
    }

    section._spcHotspotHideTimers?.forEach((timer) => window.clearTimeout(timer));
    section._spcHotspotHideTimers = null;

    section._spcHotspotSwipers?.forEach((swiper) => {
      if (swiper && !swiper.destroyed) {
        swiper.destroy(true, true);
      }
    });
    section._spcHotspotSwipers = [];
  }

  function initScrollProductCarouselHotspots(section) {
    const modal = section.querySelector('.spc-hotspot-modal');
    if (!modal) return;

    if (typeof Swiper === 'undefined') {
      window.setTimeout(() => initScrollProductCarouselHotspots(section), 100);
      return;
    }

    destroyScrollProductCarouselHotspots(section);

    section._spcHotspotHandlers = [];
    section._spcHotspotSwipers = [];

    const groups = Array.from(modal.querySelectorAll('.spc-hotspot-modal__group'));
    const activeHotspotClass = 'is-active';

    groups.forEach((group) => {
      const swiperEl = group.querySelector('.spc-hotspot-modal__swiper');
      if (!swiperEl || typeof Swiper === 'undefined') return;

      const swiper = new Swiper(swiperEl, {
        slidesPerView: 1,
        loop: swiperEl.querySelectorAll('.swiper-slide').length > 1,
        speed: 450,
        navigation: {
          prevEl: group.querySelector('.spc-hotspot-modal__nav--prev'),
          nextEl: group.querySelector('.spc-hotspot-modal__nav--next'),
        },
        pagination: {
          el: group.querySelector('.spc-hotspot-modal__pagination'),
          clickable: true,
        },
      });

      section._spcHotspotSwipers.push(swiper);
    });

    const closeModal = () => {
      modal.hidden = true;
      document.body.classList.remove('spc-hotspot-modal-open');
      groups.forEach((group) => {
        group.hidden = true;
      });
      section.querySelectorAll('.spc-hotspot').forEach((hotspot) => {
        hotspot.classList.remove(activeHotspotClass);
      });
    };

    const openModal = (productIndex, hotspotId) => {
      const targetGroup = groups.find(
        (group) => Number(group.dataset.productIndex) === Number(productIndex)
      );
      if (!targetGroup) return;

      groups.forEach((group) => {
        group.hidden = group !== targetGroup;
      });

      const groupIndex = groups.indexOf(targetGroup);
      const swiper = section._spcHotspotSwipers[groupIndex];
      const slideIndex = Array.from(targetGroup.querySelectorAll('.swiper-slide')).findIndex(
        (slide) => slide.dataset.hotspotId === hotspotId
      );

      modal.hidden = false;
      document.body.classList.add('spc-hotspot-modal-open');

      if (swiper) {
        swiper.update();
        const targetSlide = slideIndex >= 0 ? slideIndex : 0;
        if (swiper.params.loop) {
          swiper.slideToLoop(targetSlide, 0, false);
        } else {
          swiper.slideTo(targetSlide, 0, false);
        }
      }
    };

    const addHandler = (element, type, handler) => {
      element.addEventListener(type, handler);
      section._spcHotspotHandlers.push({ element, type, handler });
    };

    section.querySelectorAll('[data-spc-hotspot-close]').forEach((button) => {
      addHandler(button, 'click', closeModal);
    });

    section.querySelectorAll('.spc-hotspot').forEach((hotspot) => {
      const productIndex = hotspot.dataset.productIndex;
      const hotspotId = hotspot.dataset.hotspotId;
      const openButton = hotspot.querySelector('.spc-hotspot__open');
      const tooltip = hotspot.querySelector('.spc-hotspot__tooltip');
      let hideTimer = null;
      section._spcHotspotHideTimers = section._spcHotspotHideTimers || [];

      const showHotspot = () => {
        if (!DESKTOP_MQ.matches) return;
        if (hideTimer) {
          window.clearTimeout(hideTimer);
          hideTimer = null;
        }
        hotspot.classList.add(activeHotspotClass);
        if (tooltip) tooltip.setAttribute('aria-hidden', 'false');
      };

      const scheduleHideHotspot = () => {
        if (!DESKTOP_MQ.matches) return;
        if (hideTimer) window.clearTimeout(hideTimer);
        hideTimer = window.setTimeout(() => {
          hotspot.classList.remove(activeHotspotClass);
          if (tooltip) tooltip.setAttribute('aria-hidden', 'true');
          hideTimer = null;
        }, 220);
        section._spcHotspotHideTimers.push(hideTimer);
      };

      addHandler(hotspot, 'mouseenter', showHotspot);
      addHandler(hotspot, 'mouseleave', scheduleHideHotspot);

      if (tooltip) {
        addHandler(tooltip, 'mouseenter', showHotspot);
        addHandler(tooltip, 'mouseleave', scheduleHideHotspot);
      }

      addHandler(hotspot, 'click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!DESKTOP_MQ.matches) {
          section.querySelectorAll('.spc-hotspot').forEach((item) => {
            const isActive = item === hotspot;
            item.classList.toggle(activeHotspotClass, isActive);
            item.querySelector('.spc-hotspot__tooltip')?.setAttribute(
              'aria-hidden',
              isActive ? 'false' : 'true'
            );
          });
        }
      });

      if (openButton) {
        addHandler(openButton, 'click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          openModal(productIndex, hotspotId);
        });
      }
    });

    section._spcHotspotKeyHandler = (event) => {
      if (event.key === 'Escape' && !modal.hidden) {
        closeModal();
      }
    };

    document.addEventListener('keydown', section._spcHotspotKeyHandler);
  }

  function initScrollProductCarousel(root) {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      setTimeout(() => initScrollProductCarousel(root), 100);
      return;
    }

    gsap.registerPlugin(ScrollTrigger);

    const sections =
      root && root.classList && root.classList.contains('spc-section')
        ? [root]
        : Array.from((root && root.querySelectorAll ? root : document).querySelectorAll('.spc-section'));

    sections.forEach((section) => {
      destroyScrollProductCarousel(section);

      const desktop = section.querySelector('.spc-desktop');
      const pinPanel = section.querySelector('.spc-pin-panel');
      const scrollTrack = section.querySelector('.spc-scroll-track');
      const specsPanels = section.querySelectorAll('.spc-specs-panel');
      const products = section.querySelectorAll('.spc-product');
      const navItems = section.querySelectorAll('.spc-nav-item');
      const totalSlides = specsPanels.length;

      if (totalSlides === 0 || !desktop || !pinPanel) return;

      section._spcTriggers = [];
      section._spcNavHandlers = [];
      let activeIndex = 0;
      let pinTrigger = null;
      let suppressScrollSync = false;
      let navScrollId = 0;
      let navTargetIndex = null;
      let lastMobileSlideSwitchAt = 0;

      function setActiveSlide(index) {
        const nextIndex = Math.max(0, Math.min(totalSlides - 1, index));
        if (nextIndex === activeIndex && section.dataset.spcInitialized === 'true') return;

        activeIndex = nextIndex;

        specsPanels.forEach((el, i) => {
          el.classList.toggle('active', i === activeIndex);
        });

        products.forEach((el, i) => {
          el.classList.toggle('active', i === activeIndex);
        });

        navItems.forEach((el, i) => {
          el.classList.toggle('active', i === activeIndex);
        });

        if (!DESKTOP_MQ.matches && isMobileScrollEnabled(section)) {
          navItems[activeIndex]?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center',
          });
        }
      }

      function releaseNavScrollSync(scrollId, targetIndex) {
        if (scrollId !== navScrollId) return;

        if (section._spcNavReleaseTimer) {
          window.clearTimeout(section._spcNavReleaseTimer);
        }

        section._spcNavReleaseTimer = window.setTimeout(() => {
          if (scrollId !== navScrollId) return;

          setActiveSlide(targetIndex);
          navTargetIndex = null;
          suppressScrollSync = false;
          section._spcNavReleaseTimer = null;
        }, 120);
      }

      function setPinTop() {
        pinPanel.style.top = `${getPinOffset(section)}px`;
      }

      function enforcePinOffset(self) {
        const isActive = self?.isActive ?? pinTrigger?.isActive;
        if (!isActive) return;
        setPinTop();
      }

      function setupDesktopPin() {
        if (!DESKTOP_MQ.matches) return;

        applyHeaderOffset(section);
        setActiveSlide(0);

        if (totalSlides <= 1) return;

        pinTrigger = ScrollTrigger.create({
          trigger: desktop,
          start: () => `top top+=${getPinOffset(section)}`,
          end: () => getPinScrollEnd(section, totalSlides),
          pin: pinPanel,
          pinSpacing: true,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          snap: {
            snapTo: (progress) => getStepSnapPoint(progress, activeIndex, totalSlides, section),
            duration: { min: 0.2, max: 0.5 },
            delay: 0.08,
            ease: 'power1.inOut',
          },
          onUpdate(self) {
            if (suppressScrollSync) {
              if (navTargetIndex !== null) setActiveSlide(navTargetIndex);
              return;
            }

            const nextIndex = getNextStepIndex(self.progress, activeIndex, totalSlides, section);
            if (nextIndex !== activeIndex) {
              setActiveSlide(nextIndex);
            }
          },
          onEnter: setPinTop,
          onEnterBack: setPinTop,
          onRefresh: enforcePinOffset,
        });

        section._spcTriggers.push(pinTrigger);
      }

      function setupMobilePin() {
        if (DESKTOP_MQ.matches || !isMobileScrollEnabled(section)) return;

        applyHeaderOffset(section);
        setActiveSlide(0);

        if (totalSlides <= 1) return;

        pinTrigger = ScrollTrigger.create({
          trigger: desktop,
          start: () => `top top+=${getPinOffset(section)}`,
          end: () => getPinScrollEnd(section, totalSlides),
          pin: pinPanel,
          pinSpacing: true,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          snap: {
            snapTo: (progress) => getStepSnapPoint(progress, activeIndex, totalSlides, section),
            duration: { min: 0.15, max: 0.4 },
            delay: 0.04,
            ease: 'power1.inOut',
          },
          onUpdate(self) {
            if (suppressScrollSync) {
              if (navTargetIndex !== null) setActiveSlide(navTargetIndex);
              return;
            }

            const nextIndex = getNextStepIndex(self.progress, activeIndex, totalSlides, section);
            if (nextIndex === activeIndex) return;

            const now = performance.now();
            if (now - lastMobileSlideSwitchAt < getMobileSwitchDelay(section)) return;

            lastMobileSlideSwitchAt = now;
            setActiveSlide(nextIndex);
          },
          onEnter: setPinTop,
          onEnterBack: setPinTop,
          onRefresh: enforcePinOffset,
        });

        section._spcTriggers.push(pinTrigger);
      }

      navItems.forEach((button, index) => {
        const handler = () => {
          const scrollId = ++navScrollId;
          suppressScrollSync = true;
          navTargetIndex = index;
          setActiveSlide(index);
          scrollToSlide(pinTrigger, index, totalSlides, () => releaseNavScrollSync(scrollId, index), section);
        };

        button.addEventListener('click', handler);
        section._spcNavHandlers.push({ button, handler });
      });

      setupDesktopPin();
      setupMobilePin();
      initScrollProductCarouselHotspots(section);
      section.dataset.spcInitialized = 'true';

      requestAnimationFrame(() => {
        ScrollTrigger.refresh();
      });

      const images = section.querySelectorAll('img');
      images.forEach((img) => {
        if (img.complete) {
          ScrollTrigger.refresh();
        } else {
          img.addEventListener('load', () => ScrollTrigger.refresh(), { once: true });
        }
      });

      if (window.ResizeObserver) {
        section._spcResizeObserver = new ResizeObserver(() => {
          applyHeaderOffset(section);
          ScrollTrigger.refresh();
          enforcePinOffset(pinTrigger);
        });
        section._spcResizeObserver.observe(section);

        if (scrollTrack) {
          section._spcResizeObserver.observe(scrollTrack);
        }

        section._spcResizeObserver.observe(pinPanel);

        const stickyHeader = document.querySelector('sticky-header');
        if (stickyHeader) {
          section._spcHeaderObserver = new ResizeObserver(() => {
            applyHeaderOffset(section);
            ScrollTrigger.refresh();
            enforcePinOffset(pinTrigger);
          });
          section._spcHeaderObserver.observe(stickyHeader);
        }
      }

      const handleMediaChange = () => {
        const container = section.closest('.shopify-section') || document;
        destroyScrollProductCarousel(section);
        initScrollProductCarousel(container);
      };

      if (DESKTOP_MQ.addEventListener) {
        DESKTOP_MQ.addEventListener('change', handleMediaChange);
      } else {
        DESKTOP_MQ.addListener(handleMediaChange);
      }

      section._spcMediaHandler = () => {
        if (DESKTOP_MQ.removeEventListener) {
          DESKTOP_MQ.removeEventListener('change', handleMediaChange);
        } else {
          DESKTOP_MQ.removeListener(handleMediaChange);
        }
      };
    });

    ScrollTrigger.refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initScrollProductCarousel(document));
  } else {
    initScrollProductCarousel(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('.spc-section')) {
      initScrollProductCarousel(event.target);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('.spc-section').forEach(destroyScrollProductCarousel);
  });
})();
